/** Shared browser/server doorway geometry. Local +Z is the front; Y is up. */
import {MOVEMENT,flightAllowed} from './movement.mjs';
import {isSafePortalExit} from './spawn-safety.mjs';
export const PORTAL_DEFAULTS=Object.freeze({width:3.2,height:3.4,yaw:0,aperture:'rectangle',cooldown:0.18});
const finite=(v,fallback=0)=>typeof v==='number'&&Number.isFinite(v)?v:fallback;
const worldAt=(worlds,id)=>worlds instanceof Map?worlds.get(id):worlds?.[id];
const wrap=angle=>{const result=Math.atan2(Math.sin(angle),Math.cos(angle));return Math.abs(result)<1e-12?0:result;};
export function portalToLocal(point,portal) {
  const x=point.x-portal.position.x,z=point.z-portal.position.z,c=Math.cos(portal.yaw??0),s=Math.sin(portal.yaw??0);
  return {x:c*x-s*z,y:point.y-portal.position.y,z:s*x+c*z};
}
export function portalToWorld(point,portal) {
  const c=Math.cos(portal.yaw??0),s=Math.sin(portal.yaw??0);
  return {x:portal.position.x+c*point.x+s*point.z,y:portal.position.y+point.y,z:portal.position.z-s*point.x+c*point.z};
}
/** Orient the entry face without reflecting handedness or scaling bodies. */
export function portalEntryFrame(portal,side=1) {return side<0?{...portal,yaw:(portal.yaw??0)+Math.PI}:portal;}
export function portalApproachSide(point,portal) {return portalToLocal(point,portal).z<0?-1:1;}
export function portalHeadingDelta(source,target,sourceSide=1,targetSide=1) {return wrap((portalEntryFrame(target,targetSide).yaw??0)+Math.PI-(portalEntryFrame(source,sourceSide).yaw??0));}
export function transformPortalPoint(point,source,target,sourceSide=1,targetSide=1) {
  const local=portalToLocal(point,portalEntryFrame(source,sourceSide));
  return portalToWorld({x:-local.x,y:local.y,z:-local.z},portalEntryFrame(target,targetSide));
}
export function transformPortalVector(vector,source,target,sourceSide=1,targetSide=1) {
  const angle=portalHeadingDelta(source,target,sourceSide,targetSide),c=Math.cos(angle),s=Math.sin(angle);
  return {x:c*vector.x+s*vector.z,y:vector.y,z:-s*vector.x+c*vector.z};
}
const samePlane=(a,b)=>Math.hypot(a.position.x-b.position.x,a.position.y-b.position.y,a.position.z-b.position.z)<0.05&&Math.abs(wrap((a.yaw??0)-(b.yaw??0)))<0.01;
/** An overlapping Atelier doorway has one selected destination for this player. */
export function selectedPortals(world,activePortalId) {
  const groups=[];
  for(const portal of world.portals??[]) {
    const group=groups.find(items=>samePlane(items[0],portal));
    if(group)group.push(portal);else groups.push([portal]);
  }
  return groups.map(group=>group.find(portal=>portal.id===activePortalId)??group[0]);
}
export function resolvePortalPair(world,portal,worlds) {
  if(!portal?.targetPortalId)return null;
  const destination=worldAt(worlds,portal.targetWorldId);
  const target=destination?.portals?.find(item=>item.id===portal.targetPortalId);
  if(!target||target.targetWorldId!==world.id||target.targetPortalId!==portal.id)return null;
  return {source:portal,target,world:destination};
}
/** A reciprocal return must undo the actual entry face, including back-face
 * entries. The receipt is private simulation state even after its public body
 * metadata expires; camera distance must not change doorway topology. */
function returnsThroughEntry(world,pair,sourceSide,receipt) {
  return receipt && [1,-1].includes(receipt.sourceSide) && [1,-1].includes(receipt.targetSide) &&
    receipt.targetWorldId===world.id && receipt.sourceWorldId===pair.world.id &&
    receipt.targetPortalId===pair.source.id && receipt.sourcePortalId===pair.target.id &&
    sourceSide===receipt.targetSide;
}
export function portalDestinationSide(world,pair,sourceSide,receipt) {
  return returnsThroughEntry(world,pair,sourceSide,receipt)?receipt.sourceSide:1;
}
/** Conservative body envelope inside a rectangle or rounded arch opening. */
export function fitsPortalAperture(point,width,height,radius=MOVEMENT.radius,bodyHeight=MOVEMENT.height,aperture=PORTAL_DEFAULTS.aperture) {
  if(![point.x,point.y,width,height].every(Number.isFinite)||width<=0||height<=0)return false;
  const x=Math.abs(point.x)+radius,archRadius=Math.min(width/2,height),springline=height-archRadius;
  if(point.y<-1e-6||x>width/2+1e-6)return false;
  const ceiling=aperture==='arch'?springline+archRadius*Math.sqrt(Math.max(0,1-(x/(width/2))**2)):height;
  return point.y+bodyHeight<=ceiling+1e-6;
}
export function portalSegmentIntersection(previous,next,source,target=source) {
  const a=portalToLocal(previous,source),b=portalToLocal(next,source);
  if(!((a.z>0&&b.z<=0)||(a.z<0&&b.z>=0)))return null;
  const fraction=a.z/(a.z-b.z),point={x:a.x+(b.x-a.x)*fraction,y:a.y+(b.y-a.y)*fraction,z:0};
  const width=Math.min(source.width??PORTAL_DEFAULTS.width,target.width??PORTAL_DEFAULTS.width);
  const height=Math.min(source.height??PORTAL_DEFAULTS.height,target.height??PORTAL_DEFAULTS.height);
  return [source,target].every(portal=>fitsPortalAperture(point,width,height,MOVEMENT.radius,MOVEMENT.height,portal.aperture??PORTAL_DEFAULTS.aperture))?{fraction,point}:null;
}
/** Conservative body aperture used only to bound remote crossing metadata. */
export function portalTraversalInAperture(player,worlds) {
  const receipt=player?.portalTraversal,world=worldAt(worlds,player?.worldId);
  if(!receipt||receipt.targetWorldId!==world?.id)return false;
  const portal=world.portals?.find(p=>p.id===receipt.targetPortalId),pair=portal&&resolvePortalPair(world,portal,worlds);
  if(!pair||pair.world.id!==receipt.sourceWorldId||pair.target.id!==receipt.sourcePortalId)return false;
  const p=portalToLocal(player.position,portal),width=Math.min(portal.width??PORTAL_DEFAULTS.width,pair.target.width??PORTAL_DEFAULTS.width),height=Math.min(portal.height??PORTAL_DEFAULTS.height,pair.target.height??PORTAL_DEFAULTS.height);
  return Math.abs(p.z)<=1.5&&Math.abs(p.x)<=width/2+1.2&&p.y>=-MOVEMENT.height&&p.y<=height;
}
/** Run after shared movement; only the actual collision-resolved segment crosses. */
export function stepPortalCrossing(previousPlayer,movedPlayer,world,worlds,elapsed,options={}) {
  const player={...movedPlayer,portalCooldownRemaining:Math.max(0,finite(movedPlayer.portalCooldownRemaining)-Math.max(0,finite(elapsed)))};
  if(player.portalTraversal&&!player.portalTraversal.publicComplete&&!portalTraversalInAperture(player,worlds))player.portalTraversal={...player.portalTraversal,publicComplete:true};
  if(previousPlayer.worldId&&previousPlayer.worldId!==world.id)return {player,crossing:null};
  const candidates=[];
  for(const source of selectedPortals(world,player.activePortalId)) {
    const pair=resolvePortalPair(world,source,worlds);
    const intersection=portalSegmentIntersection(previousPlayer.position,player.position,source,pair?.target??source);
    if(!intersection)continue;
    if(source.id===player.lastPortalId&&player.portalCooldownRemaining>0) {
      // Cooldown suppresses numerical/outward bounce, not a deliberate native
      // reversal. Skipping an inward sweep strands the body behind this plane
      // forever because no later frame crosses it again.
      const side=portalApproachSide(previousPlayer.position,source),distance=Math.abs(portalToLocal(previousPlayer.position,source).z);
      const inward=(finite(player.velocityX)*Math.sin(source.yaw??0)+finite(player.velocityZ)*Math.cos(source.yaw??0))*side< -1e-6;
      if(!pair||distance<=1e-7||!inward||!returnsThroughEntry(world,pair,side,previousPlayer.portalTraversal))continue;
    }
    candidates.push({source,...pair,...intersection});
  }
  candidates.sort((a,b)=>a.fraction-b.fraction);
  const blocked=pair=>{
    const before=previousPlayer.position,after=player.position,length=Math.hypot(after.x-before.x,after.y-before.y,after.z-before.z);
    const fraction=Math.max(0,pair.fraction-.002/Math.max(length,.002));
    const nx=Math.sin(pair.source.yaw??0),nz=Math.cos(pair.source.yaw??0),normalSpeed=finite(player.velocityX)*nx+finite(player.velocityZ)*nz;
    return {player:{...player,position:{x:before.x+(after.x-before.x)*fraction,y:before.y+(after.y-before.y)*fraction,z:before.z+(after.z-before.z)*fraction},velocityX:finite(player.velocityX)-normalSpeed*nx,velocityZ:finite(player.velocityZ)-normalSpeed*nz},crossing:null,blockedPortalId:pair.source.id};
  };
  for(const pair of candidates) {
    if(!pair.target||options.blockedPortalWorldIds?.includes(pair.world.id))return blocked(pair);
    const sourceSide=portalApproachSide(previousPlayer.position,pair.source),targetSide=portalDestinationSide(world,pair,sourceSide,previousPlayer.portalTraversal);
    const headingDelta=portalHeadingDelta(pair.source,pair.target,sourceSide,targetSide);
    const velocity=transformPortalVector({x:finite(player.velocityX),y:finite(player.velocityY),z:finite(player.velocityZ)},pair.source,pair.target,sourceSide,targetSide);
    const transformed={...player,worldId:pair.world.id,position:transformPortalPoint(player.position,pair.source,pair.target,sourceSide,targetSide),velocityX:velocity.x,velocityY:velocity.y,velocityZ:velocity.z,yaw:wrap(finite(player.yaw)+headingDelta),activePortalId:pair.target.id,lastPortalId:pair.target.id,portalCooldownRemaining:PORTAL_DEFAULTS.cooldown};
    transformed.portalTraversal={sequence:(previousPlayer.portalTraversal?.sequence??0)+1,sourceWorldId:world.id,targetWorldId:pair.world.id,sourcePortalId:pair.source.id,targetPortalId:pair.target.id,sourceSide,targetSide};
    if(player.flying===true)transformed.flying=flightAllowed(pair.world);
    if(!isSafePortalExit(transformed.position,pair.world,{...options,flying:transformed.flying===true}))return blocked(pair);
    // Jump timing and vertical velocity survive the doorway; support is recomputed
    // by stepMovement in the destination on the next tick.
    return {player:transformed,crossing:{sourcePortalId:pair.source.id,targetPortalId:pair.target.id,sourceSide,targetSide,headingDelta,world:pair.world}};
  }
  return {player,crossing:null};
}
